import React, {Component} from 'react';
import PropTypes from 'prop-types';

import {withStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import LinearProgress from '@material-ui/core/LinearProgress';
import SvgNotFound from '@material-ui/icons/Block';
import SvgExpand from '@material-ui/icons/ExpandMore';

import {helper, http, api} from '../../common/common';
import SelectRegion from '../../controls/SelectRegion';
import Card from './Card'

const styles = (theme) => {
    return {
        panel: {
            position: 'absolute',
            top: 16,
            bottom: 16,
            left: 16,
            width : 420,
            textAlign: 'left',
            overflow: 'hidden',
        },
        header: {
            position: 'relative',
            width: '100%',
            height: 102,
        },
        headerDivider: {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 1,
        },
        label: {
            position: 'absolute',
            top: 16,
            left: 26,
            right: 26,
            fontWeight: 700,
            fontVariant: 'small-caps',
            cursor: 'default',
        },
        selectRegion: {
            position: 'absolute',
            bottom: 15,
            left: 26,
            height: 28,
            lineHeight: '28px',
        },
        previewPanel: {
            position: 'absolute',
            top: 102,
            bottom: 0,
            left: 0,
            right: 0,
            overflow: 'auto',
        },
        loadingContainer: {
            position: 'absolute',
            top: 'calc(50% - 60px)',
            left: 'calc(50% - 80px)',
            width: 160,
            height: 120,
            textAlign: 'center',
        },
        loadingLabel: {
            marginTop: 16,
            color: theme.palette.text.disabled,
        },
        loadingProgress: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
        },
        notFoundContainer: {
            position: 'absolute',
            top: 'calc(50% - 60px)',
            left: 'calc(50% - 80px)',
            width: 160,
            height: 120,
            textAlign: 'center',
        },
        notFound: {
            margin: 'auto',
            width: 60,
            height: 60,
            color: theme.palette.text.disabled,
        },
        notFoundLabel: {
            marginTop: 16,
            color: theme.palette.text.disabled,
        },
        expandMorePanel: {
            width: '100%',
            height: 24,
            textAlign: 'center',
        },
        expandMoreIcon: {
            ...theme.iconStyle.size[18],
            ...theme.iconStyle.dark,
            cursor: 'pointer',
        }
    };
}

class Sidebar extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selection: 0,
            selectedCardUid: 0,
            loading: false,
            cardCount: 0,
        };

        this._cancelToken = http.CancelToken.source();
        this._cardList = [];
        this._date = new Date();
        this._year = this._date.getFullYear();
        this._mth = this._date.getMonth() + 1;
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        const overlayCancel = (this.props.overlayInfo.uid && !nextProps.overlayInfo.uid);

        return (this.state.selection !== nextState.selection ||
            this.state.selectedCardUid !== nextState.selectedCardUid ||
            this.state.loading !== nextState.loading ||
            this.state.cardCount !== nextState.cardCount ||
            this.props.regionListTimestamp !== nextProps.regionListTimestamp ||
            overlayCancel);
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (prevProps.regionListTimestamp !== this.props.regionListTimestamp) {
            if (this.props.regionList.length > 0) {
                const selectionId = (this.props.regionDefaultIndex) ? this.props.regionDefaultIndex : 0;

                this.setState({
                    selection: selectionId,
                    selectedCardUid: 0,
                    loading: true,
                });

                this._queryEntryList(selectionId);
            }
        }

        if (prevProps.overlayInfo.uid && !this.props.overlayInfo.uid) {
            this.setState({
                selectedCardUid: 0,
            });
        }
    }

    componentWillUnmount = () => {
        this._endQueryTimer();

        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    render = () => {
        const {classes} = this.props;

        return (
            <div>
                <Paper className={classes.panel}>
                    <div className={classes.header}>
                        <Typography
                            className={classes.label}
                            align='left'
                            variant='h5'
                        >
                            Request Imagery
                        </Typography>
                        {this._renderSelectRegionDropdown()}
                        {this._renderDividerProgress()}
                    </div>
                    {this._renderPreviewCards()}
                </Paper>
            </div>
        );
    }

    _renderSelectRegionDropdown = () => {
        const {classes} = this.props;

        return (
            <SelectRegion
                className={classes.selectRegion}
                regionListTimestamp={this.props.regionListTimestamp}
                regionList={this.props.regionList}
                regionIndex={this.state.selection}
                disabled={this.state.loading}
                onSelect={this._handleSelection}
                onError={this.props.onError}
            />
        );
    }

    _renderDividerProgress = () => {
        const {classes} = this.props;

        if (!this.state.loading) {
            return (
                <Divider className={classes.headerDivider}/>
            );
        }

        return (
            <LinearProgress
                className={classes.loadingProgress}
                color={'secondary'}
            />
        );
    }

    _renderPreviewCards = () => {
        const {classes} = this.props;

        const initalLoading = (this.state.loading && !this._cardList.length)

        if (initalLoading || this.props.regionListTimestamp === 0) {
            const text = (this.state.loading) ? 'Loading images...' : 'Loading regions...';
            return this._renderLoadingSpinner(text);
        }

        const cards = this._generateCards();

        if (cards.length === 0) {
            return this._renderNotFound();
        }

        return (
            <div className={classes.previewPanel}>
                {cards}
                {this._renderExpandMore()}
            </div>
        );
    }

    _renderLoadingSpinner = (text) => {
        const {classes} = this.props;

        return (
            <div className={classes.previewPanel}>
                <div className={classes.loadingContainer}>
                    <Typography
                        className={classes.loadingLabel}
                        align='center'
                        variant='button'
                    >
                        {text}
                    </Typography>
                </div>
            </div>
        );
    }

    _renderNotFound = () => {
        const {classes} = this.props;

        return (
            <div className={classes.previewPanel}>
                <div className={classes.notFoundContainer}>
                    <SvgNotFound className={classes.notFound}/>
                    <Typography
                        className={classes.notFoundLabel}
                        align='center'
                        variant='button'
                    >
                        No images found
                    </Typography>
                </div>
            </div>
        )
    }

    _renderExpandMore = () => {
        const {classes} = this.props;

        if (!this._checkValidDate() || this.state.loading) {
            return null;
        }

        return (
            <div className={classes.expandMorePanel}>
                <div onClick={this._handleLoadMore}>
                    <SvgExpand className={classes.expandMoreIcon}/>
                </div>
            </div>
        );
    }

    _startQueryTimer = () => {
        if (this._loadingTimer) {
            clearTimeout(this._loadingTimer);
        }

        this._loadingTimeExceed = false;

        this._loadingTimer = setTimeout(() => {
            this._loadingTimeExceed = true;

            if (this._cardList.length === 0) {
                this._resetDate();
            }
        }, 15000);
    }

    _endQueryTimer = () => {
        if (this._loadingTimer) {
            clearTimeout(this._loadingTimer);
        }
    }

    _queryEntryList = (selectionId) => {
        const region = this.props.regionList[selectionId];

        this._cardList = [];

        this._resetDate();

        if (!region) {
            return;
        }

        this._startQueryTimer();
        this._requestEntryListSection(region);
    }

    _generateEntryQuery = (region, e) => {
        this._cardList.push({
            uid: region.properties.key + "-" + e.mode + "-" + e.date,
            mode: e.mode,
            category: this._modeToCategory(e.mode),
            date: e.date,
            cloudCover: 0,
            content: [],
        });

        const card = this._cardList[this._cardList.length - 1];

        const query = [];

        for (const instance of e.instances) {
            card.content.push({
                key: instance.key,
                bounds: instance.bounds,
            });

            this._signalPartialDone();

            query.push(this._requestEntryCard(card, instance));
        }

        return query;
    }

    _generateCards = () => {
        if (!this._cardList) {
            return [];
        }

        const cards = [];

        for (const c of this._cardList) {
            cards.push(<Card
                getAccessCode={this.props.getAccessCode}
                key={c.uid}
                selectedUid={this.state.selectedCardUid}
                info={c}
                onClick={this._handleCardClick}
            />);
        };

        return cards;
    }

    _handleSelection = (value) => {
        this.setState({
            selection: value,
            selectedCardUid: 0,
            loading: true,
        });

        this._cardList = [];

        this._queryEntryList(value);

        if (this.props.onChangeRegion) {
            this.props.onChangeRegion(value);
        }
    }

    _handleLoadMore = () => {
        this.setState({
            loading: true,
        });

        const region = this.props.regionList[this.state.selection];

        this._requestEntryListSection(region);
    }

    _handleCardClick = (info) => {
        this.setState({
            selectedCardUid: info.uid,
        });

        if (this.props.onSelectCard) {
            this.props.onSelectCard(info);
        }
    }

    _signalPartialDone = () => {
        this.setState({
            loading: true,
            cardCount: this._cardList.length,
        });
    }

    _signalDone = () => {
        this.setState({
            loading: false,
            cardCount: this._cardList.length,
        });
    }

    _resetDate = () => {
        this._year = this._date.getFullYear();
        this._mth = this._date.getMonth() + 1;
    }

    _checkValidDate = () => {
        return (this._year > 2016);
    }

    _loadNextMonth = () => {
        this._mth = (this._mth === 1) ? 12 : (this._mth - 1);

        if (this._mth === 12) {
            this._year = this._year - 1;
        }
    }

    _modeToCategory = (mode) => {
        switch (mode) {
            case 's2':
                return 'mid_res';
            default:
                return 'low_res';
        }
    }

    _processError = (error) => {
        this._signalDone();

        if (error.response && error.response.status === 404) {
            return;
        }

        if (this.props.onError) {
            this.props.onError(error);
        }
    }

    _requestEntryListSection = async (region) => {
        try {
            const response = await http.get(api.tasking() + '/entry', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'organization': region.properties.organization,
                    'key': 'data/' + region.properties.key,
                    'year': this._year,
                    'month': this._mth,
                },
                cancelToken: this._cancelToken.token,
            });

            var respQuery = [];

            for (const e of response.data.content) {
                respQuery = respQuery.concat(this._generateEntryQuery(region, e));
            }

            await Promise.all(respQuery);

            this._loadNextMonth();

            if (this._cardList.length <= 8 && !this._loadingTimeExceed && this._checkValidDate()) {
                this._requestEntryListSection(region);
            } else {
                this._endQueryTimer();
                this._signalDone();
            }
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            const e = helper.sanitizeError(error, 'Property Error', 'Unable to load property', 'warn_only');
            this._processError(e);
        }
    }

    _requestEntryCard = async (card, instance) => {
        try {
            const response = await http.get(api.tasking() + '/detail', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'entry_key': instance.key,
                },
                cancelToken: this._cancelToken.token,
            });

            card.cloudCover += (response.data.cloud_cover / 10000);
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            console.log('Missing card detail');
        }
    }
}

Sidebar.propTypes = {
    regionListTimestamp: PropTypes.number.isRequired,
    regionList: PropTypes.arrayOf(PropTypes.object).isRequired,
    regionDefaultIndex: PropTypes.number,
    overlayInfo: PropTypes.object.isRequired,
    getAccessCode: PropTypes.func.isRequired,
    onChangeRegion: PropTypes.func,
    onSelectCard: PropTypes.func,
    onError: PropTypes.func,
}

export default withStyles(styles)(Sidebar);
